// Copyright (c) Microsoft. All rights reserved.

import React, { useState, useEffect, ChangeEvent } from 'react';
import { Switch } from '../base/Switch';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import { Badge, Button } from '../base';
import { IMCPTool } from '../../libs/models/app/mcp/Tool';
import { getConnectorIcon } from '../../libs/common';
import { useApp } from '../../libs/hooks/useApp';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { outlookAuthService } from '../../libs/services/OutlookAuthService';

interface Tool {
  id: string;
  name: string;
  icon?: string;
  iconUrl?: string; // 커넥터 타입별 아이콘 URL
  enabled: boolean;
  needAttention?: boolean;
  needAttentionReason?: string;
  connectorType: string;
}


interface ToolSelectPopoverProps {
  isOpen?: boolean;
  // onToolToggle?: (toolId: string, enabled: boolean) => void;
  onOpenSettings?: () => void;
  showHeader?: boolean;
  // MCP 서버 관련 props
  localMcpTools?: Record<string, IMCPTool[]>;
  onMcpServerToggle?: (serverId: string, checked: boolean) => void;
  onMcpToolToggle?: (server: string, toolId: string, checked: boolean) => void;
  isDesktop?: boolean; // MCP 서버는 데스크탑에서만 표시
}

export const ToolSelectPopover: React.FC<ToolSelectPopoverProps> = ({
  // onToolToggle,
  onOpenSettings,
  showHeader = true,
  localMcpTools = {},
  isDesktop = true
}) => {
  const [connectors, setConnectors] = useState<Tool[]>([]);
  const [mcpConnectors, setMcpConnectors] = useState<Tool[]>([]);
  // const [userConnectors, setUserConnectors] = useState<UserConnector[]>([]);
  const [isToggling, setIsToggling] = useState<string | null>(null);
  const { connectors: userConnectors } = useAppSelector((state: RootState) => state.tools);

  // API에서 커넥터 목록 조회
  const fetchConnectors = () => {
    try {
      if (userConnectors.data) {
        const toolList: Tool[] = userConnectors.data
          .filter(c => c.userConnectorType == 'Connector')
          .map(c => ({
            id: c.id ?? c.connectorType ?? 'unknown',
            name: c.displayName ?? c.connectorType ?? 'Unknown',
            icon: 'prompt',
            iconUrl: getConnectorIcon(c.iconUri),
            enabled: c.isEnabled,
            needAttention: c.needAttention,
            needAttentionReason: c.needAttnetionReasonDetail ?? undefined,
            connectorType: c.connectorType ?? ''
          }));
        setConnectors(toolList);

        const mcp: Tool[] = userConnectors.data
          .filter(c => c.userConnectorType == 'Mcp')
          .map(c => ({
            id: c.id ?? c.connectorType ?? 'unknown',
            name: c.displayName ?? c.connectorType ?? 'Unknown',
            icon: 'prompt',
            iconUrl: getConnectorIcon(c.iconUri),
            enabled: c.isEnabled,
            needAttention: c.needAttention,
            needAttentionReason: c.needAttnetionReasonDetail ?? undefined,
            connectorType: c.connectorType ?? ''
          }));
        setMcpConnectors(mcp);
      }
    } catch (error) {
      console.error('Failed to fetch connectors:', error);
    }
  };

  // 팝오버가 열릴 때마다 호출
  useEffect(() => {
    fetchConnectors();
  }, [userConnectors]);

  const apps = useApp();
  // MCP 서버 전체 토글
  const handleLocalMcpServerToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const serverId = event.target.id;
    const checked = event.target.checked;
    apps.changedMcpServerState(serverId, checked);
  };

  const localMcpList = Object.keys(localMcpTools);
  const hasMcpServers = localMcpList.length > 0;


  const handleToggle = async (toolId: string) => {
    // 해당 UserConnector 찾기
    const userConnector = userConnectors.data.find(c => c.id === toolId);
    if (!userConnector) {
      console.error('UserConnector not found:', toolId);
      return;
    }

    const newEnabled = !userConnector.isEnabled;
    setIsToggling(toolId);

    // UI 즉시 업데이트 (낙관적 업데이트)
    setConnectors(prev => prev.map(t => t.id === toolId ? { ...t, enabled: newEnabled } : t));

    try {
      // PUT API 호출
      await apps.updateConnector({
        ...userConnector,
        isEnabled: newEnabled
      }).then(() => {
        // onToolToggle?.(toolId, newEnabled);
      });
    } catch (error) {
      console.error('Failed to update connector:', error);
      // 실패 시 롤백
      setConnectors(prev => prev.map(t => t.id === toolId ? { ...t, enabled: !newEnabled } : t));
    } finally {
      setIsToggling(null);
    }
  };

  // 커넥터 추가 핸들러 (MS 인증 포함)
  const handleAddConnector = (tool: Tool) => {
    // setIsAdding(tool.id);
    try {
      // 1. MS 인증이 필요한 커넥터인지 확인 (이름 기반 체크)
      const lowerId = tool.connectorType.toLowerCase();
      const msKeywords = ['outlook', 'onedrive', 'teams', 'onenote', 'todo'];

      const attlassian = ['confluence'];

      if (msKeywords.some(keyword => lowerId.includes(keyword))) {
        console.log('Opening auth popup for:', tool.id);
        // 팝업 열기
        const popup = outlookAuthService.registerConnectorPopup(tool.connectorType);
        if (popup) {
          // setAuthPopup(popup);
        } else {

          alert('팝업 차단을 해제해주세요.');
        }

        return;
      } else if (attlassian.some(keyword => lowerId.includes(keyword))) {
        apps.registerConfluenceMcp(tool.connectorType);
      }

      // 2. 일반 커넥터: API 호출로 생성
      //await apps.createConnector(connector);

      // 목록 새로고침 (MS 인증이 아닌 경우) - 전체 동기화로 변경하여 상태 확실히 반영
      // await syncAndFetchAll();
    } catch (error) {
      console.error('Failed to add connector:', error);
      alert('도구 추가에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      // setIsAdding(null);
    }
  };

  return (
    <ul className="tool-select-popover-content" role="listbox">
      {showHeader && (
        <div className="tool-select-popover-header">
          <h3 className="tool-select-popover-title">도구</h3>
          <button
            className="tool-select-popover-detail-link"
            onClick={() => {
              onOpenSettings?.();
            }}
            type="button"
          >
            <Icon name="setting" />
            <span>상세 보기</span>
          </button>
        </div>
      )}

      {/* 통합 스크롤 영역 - 단일 div */}
      <div className="tool-select-popover-scroll-area">
        {/* 커넥터 섹션 타이틀 */}
        {connectors.length > 0 && (
          <div className="tool-select-popover-section-title">커넥터</div>
        )}

        {/* 커넥터 아이템들 */}
        {connectors.map((connector) => (
          <div key={connector.id} className={`tool-select-popover-item ${connector.needAttention ? 'need-attention' : ''}`} data-tool-id={connector.id} role="option" aria-selected={connector.enabled}>
            <div className="tool-select-popover-item-left">
              <div className="tool-select-popover-item-icon">
                <img src={connector.iconUrl} alt={connector.name} className="connector-icon" />
              </div>
              <span className="tool-select-popover-item-name" title={connector.name.length > 50 ? connector.name : undefined}>
                {connector.name.length > 50 ? `${connector.name.slice(0, 50)}...` : connector.name}
              </span>
              {connector.needAttention && (
                <Tooltip message={`ⓘ ${connector.needAttentionReason ?? '확인이 필요합니다'}`} className="tool-attention-tooltip-wrap">
                  <span className="tool-attention-badge">
                    <span className="attention-icon">!</span>
                  </span>
                </Tooltip>
              )}
            </div>
            {connector.needAttention ?
              <Button size="small" variant="soft" iconOnly='refresh' onClick={() => { handleAddConnector(connector) }}> 재연결 </Button> :
              <div className="tool-select-popover-item-right">
                <Switch
                  checked={connector.enabled}
                  disabled={isToggling === connector.id || (connector.needAttention)}
                  onChange={() => {
                    void handleToggle(connector.id);
                  }}
                />
              </div>
            }
          </div>
        ))}
        {mcpConnectors.length > 0 && (
          <>
            <div
              className="tool-select-popover-section-title"
              style={{ marginTop: connectors.length > 0 ? '16px' : '0' }}
            >
              MCP 서버
            </div>

            {mcpConnectors.map((connector) => (
              <div key={connector.id} className={`tool-select-popover-item ${connector.needAttention ? 'need-attention' : ''}`} data-tool-id={connector.id} role="option" aria-selected={connector.enabled}>
                <div className="tool-select-popover-item-left">
                  <div className="tool-select-popover-item-icon">
                    <img src={connector.iconUrl} alt={connector.name} className="connector-icon" />
                  </div>
                  <span className="tool-select-popover-item-name" title={connector.name.length > 50 ? connector.name : undefined}>
                    {connector.name.length > 50 ? `${connector.name.slice(0, 50)}...` : connector.name}
                  </span>
                  {connector.needAttention && (
                    <Tooltip message={`ⓘ ${connector.needAttentionReason ?? '확인이 필요합니다'}`} className="tool-attention-tooltip-wrap">
                      <span className="tool-attention-badge">
                        <span className="attention-icon">!</span>
                      </span>
                    </Tooltip>
                  )}
                </div>
                {connector.needAttention ?
                  <Button size="small" variant="soft" iconOnly='refresh' onClick={() => { handleAddConnector(connector) }}> 재연결 </Button>
                  : <div className="tool-select-popover-item-right">
                    <Switch
                      checked={connector.enabled}
                      disabled={isToggling === connector.id || (connector.needAttention)}
                      onChange={() => {
                        void handleToggle(connector.id);
                      }}
                    />
                  </div>
                }
              </div>
            ))}
          </>

        )}
        {/* MCP 서버 섹션 타이틀 */}
        {isDesktop && hasMcpServers && (
          <>
            <div
              className="tool-select-popover-section-title"
              style={{ marginTop: connectors.length > 0 ? '16px' : '0' }}
            >
              로컬 MCP 서버
            </div>

            {localMcpList.map((server) => {
              const serverTools = localMcpTools[server] ?? [];
              const enabledCount = serverTools.filter((t) => t.enabled).length;
              const hasAnyEnabled = enabledCount > 0;

              return (
                <div
                  key={server}
                  className="tool-select-popover-item mcp-server-item"
                  data-server-id={server}
                  onClick={() => {
                    //goToMcpDetail(server);
                  }}
                  style={{ cursor: 'pointer' }}
                  role="option"
                  aria-selected={hasAnyEnabled}
                >
                  <div className="tool-select-popover-item-left">
                    <div className="tool-select-popover-item-icon mcp-badge">
                      <Badge accent="normal">{server.slice(0, 1).toUpperCase()}</Badge>
                    </div>
                    <span className="tool-select-popover-item-name">{server}</span>
                  </div>

                  <div className="tool-select-popover-item-right">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Switch
                        id={server}
                        checked={hasAnyEnabled}
                        onChange={handleLocalMcpServerToggle}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* 도구가 없을 때 */}
        {connectors.length === 0 && !hasMcpServers && mcpConnectors.length == 0 && (
          <div className="tool-empty-state">
            <p className="tool-empty-message">사용 가능한 도구가 없습니다. <br /> 상세보기에서 도구를 추가해주세요.</p>
          </div>
        )}
      </div>
    </ul>
  );
};

